import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button } from '@material-ui/core';
import { TextField, Autocomplete, createFilterOptions, Alert, FormHelperText } from '@mui/material';
import { useForm, Controller, type FieldError } from 'react-hook-form';

import notes from 'assets/onboarding/notes.svg';
import { ModalTitle } from 'components/Modal/Modal';
import { ModalStep } from 'components/Flow/components/ModalStep';
import { StepComponentProps } from 'components/Flow/RenderMachine';
import Img from 'retired/Img';
import { selectOrganization, selectUser } from 'entities/user/selectors';

import { ControlledPhoneInput } from './ControlledPhoneInput';
import type { ContactFormData, Salutation, ContactField } from './types';
import { PrevNextButtons } from './PrevNextButtons';

const salutations: Salutation[] = [
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'optOutOfSalutation', label: 'Prefer not to say' },
];

const filter = createFilterOptions<Salutation>();

const contactFields: ContactField[] = [
  {
    name: 'contact.firstName',
    label: 'Contact first name',
    rules: { required: 'Please enter a valid first name' },
    required: true,
  },
  {
    name: 'contact.lastName',
    label: 'Contact last name',
    rules: { required: 'Please enter a valid last name' },
    required: true,
  },
  {
    name: 'contact.address',
    label: 'Contact address',
    rules: { required: 'Please enter a valid address' },
    required: true,
  },
];

const getNestedProperty = (obj: Record<string, unknown>, path: string): unknown => {
  if (!obj || !path) {
    return undefined;
  }

  return path.split('.').reduce((acc: unknown, key: string) => {
    if (acc && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
};

// @NOTE-EV: TODO add an email validation helper in utils if there is not one already
const isEmail = /^\S+@\S+\.\S+$/;

export const Contact = ({ send, isTransitionAllowed, state }: StepComponentProps): JSX.Element => {
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);

  const isSubmitting = Boolean(
    typeof state.value === 'object' && 'contact' in state.value
      ? state.value.contact === 'submitting'
      : false
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ContactFormData>({
    defaultValues: {
      email: organization?.email || '',
      phone: organization?.phone || '',
      contact: {
        phone: organization?.contact?.phone || '',
        salutation: organization?.contact?.salutation || '',
        firstName: organization?.contact?.firstName || '',
        lastName: organization?.contact?.lastName || '',
        address: organization?.contact?.address || '',
      },
    },
  });

  const [salutationsOptions, setSalutationsOptions] = React.useState<Salutation[]>(salutations);

  const onSubmit = (data: ContactFormData) => {
    send({
      type: 'SUBMIT',
      data,
    });
  };
  return (
    <ModalStep
      showProgressBar
      actions={
        <div style={{ display: 'flex' }}>
          {user && user.isAdmin && (
            <PrevNextButtons
              isPrevTransitionAllowed={isTransitionAllowed}
              isNextTransitionAllowed={isTransitionAllowed}
              sendPrev={send}
              sendNext={send}
            />
          )}
          <Button
            type="button"
            variant="contained"
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
        </div>
      }
      header={<Img src={notes} />}
      headerStyles={{
        paddingTop: 16,
      }}
    >
      <Box mb={4}>
        <ModalTitle style={{ marginBottom: '16px' }}>Please provide contact information</ModalTitle>
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 20,

            height: '100%',
          }}
        >
          <Controller
            control={control}
            name="email"
            rules={{
              required: "Please provide the nonprofit's email",
              pattern: {
                value: isEmail,
                message: 'Please enter a valid email',
              },
            }}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                helperText={errors.email?.message || ''}
                error={Boolean(errors.email)}
                required
                label="Nonprofit Email"
                fullWidth
                variant="standard"
              />
            )}
          />
          <div>
            <ControlledPhoneInput<ContactFormData>
              name="phone"
              control={control}
              rules={{ required: 'Please enter a valid phone number' }}
              country={organization?.locationObject.countryCode.toLowerCase() || 'us'}
              required
              error={Boolean(errors.phone)}
              label="Nonprofit Phone Number"
            />
            {errors.phone?.message && (
              <FormHelperText error={Boolean(errors.phone?.message)}>
                {errors.phone?.message}
              </FormHelperText>
            )}
          </div>
          <div>
            <ControlledPhoneInput<ContactFormData>
              name="contact.phone"
              control={control}
              rules={{ required: 'Please enter a valid phone number' }}
              label="Contact Phone Number"
              required
              country={organization?.locationObject.countryCode.toLowerCase() || 'us'}
              error={Boolean(getNestedProperty(errors, 'contact.phone'))}
              errorMessage={
                typeof errors.contact?.phone?.message === 'string'
                  ? errors.contact?.phone?.message
                  : ''
              }
            />
            {errors.contact?.phone?.message && (
              <FormHelperText error={Boolean(errors.contact?.phone?.message)}>
                {errors.contact?.phone?.message}
              </FormHelperText>
            )}
          </div>
          <Controller
            name="contact.salutation"
            control={control}
            defaultValue={salutationsOptions[0].value}
            rules={{
              required: 'Please enter a valid salutation',
              maxLength: {
                value: 50,
                message: 'Please use less than 50 characters',
              },
            }}
            render={({ field }) => (
              <div>
                <Autocomplete
                  options={salutationsOptions}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  freeSolo
                  onChange={(_, newValue) => {
                    if (typeof newValue === 'string') {
                      setSalutationsOptions([
                        ...salutationsOptions,
                        { value: newValue.trim(), label: newValue.trim() },
                      ]);
                      field.onChange(newValue);
                    } else if (newValue) {
                      field.onChange(newValue?.value || newValue?.inputValue);
                    } else {
                      field.onChange('');
                    }
                  }}
                  getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === 'string') {
                      return (
                        salutationsOptions.find((item) => option === item.value)?.label || option
                      );
                    }
                    return option.label;
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    const isExisting = options.some((option) => inputValue === option.label);

                    if (inputValue !== '' && !isExisting) {
                      filtered.push({
                        inputValue,
                        label: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contact salutation"
                      error={Boolean(getNestedProperty(errors, 'contact.salutation'))}
                      helperText={
                        (getNestedProperty(errors, 'contact.salutation') as FieldError)?.message ||
                        "Please select an option or type to add a new one and press 'Enter'"
                      }
                      required
                      variant="standard"
                      fullWidth
                    />
                  )}
                  value={field.value}
                />
              </div>
            )}
          />
          {contactFields.map((contactField) => (
            <Controller
              control={control}
              key={contactField.name}
              name={contactField.name}
              defaultValue=""
              rules={contactField.rules}
              render={({ field }) => (
                <TextField
                  {...field}
                  helperText={(getNestedProperty(errors, contactField.name) as FieldError)?.message}
                  error={Boolean(getNestedProperty(errors, contactField.name))}
                  required={contactField.required}
                  label={contactField.label}
                  fullWidth
                  variant="standard"
                />
              )}
            />
          ))}
        </form>
        {state.context.error && (
          <Alert severity="error" style={{ marginTop: '20px' }}>
            Submission failed, please try again. If the error persists, please contact support.{' '}
          </Alert>
        )}
      </Box>
    </ModalStep>
  );
};
